<template>
    <div>
        <rkm-form
            ref="Form"
            show-legend
        >
            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-input
                        label="Unidade"
                        :value="data.form.unidade"
                        disabled
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input
                        label="Local de atendimento"
                        :value="data.form.infraestrutura"
                        disabled
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="6">
                    <rkm-form-date-picker
                        range
                        :range-inline="false"
                        start-label="Período de"
                        end-label="Até"
                        v-model="data.model.periodo"
                        :min-date="data.additionals.periodoMinDate"
                        :max-date="data.additionals.currentDate"
                        :start-validations="validations.periodo.start"
                        :end-validations="validations.periodo.end"
                    />
                </rkm-col>

                <rkm-col cols="6">
                    <rkm-form-input
                        label="Paciente"
                        v-model="data.model.paciente"
                        uppercase
                    />
                </rkm-col>
            </rkm-row>

            <rkm-row>
                <rkm-col cols="3">
                    <rkm-form-select
                        label="Sexo"
                        :options="data.form.sexos"
                        default-option-text="Todos"
                        v-model="data.model.sexoId"
                        value-field="id"
                        text-field="descricao"
                        show-default-option
                    />
                </rkm-col>

                <rkm-col cols="3">
                    <rkm-form-date-picker
                        label="Data de nascimento"
                        v-model="data.model.dataNascimento"
                        :max-date="data.additionals.currentDate"
                    />
                </rkm-col>

                <rkm-col cols="auto">
                    <rkm-button
                        :label="false"
                        color="primary"
                        @click="$refs.Form.validate(search)"
                    >
                        Buscar
                    </rkm-button>

                    <rkm-button
                        :label="false"
                        @click="resetForm()"
                    >
                        Limpar
                    </rkm-button>
                </rkm-col>
            </rkm-row>
        </rkm-form>

        <rkm-table
            :items="atendimentos"
            :fields="fields.tables.atendimentos"
            hide-filters
        >
            <template v-slot:cell(paciente)="{ item }">
                <rkm-button
                    :id="uniqueId(`paciente-icon-${item.tipoIdentificador}-${item.id}`)"
                    class="mr-2"
                    small
                >
                    <rkm-icon icon="user"/>
                </rkm-button>

                <rkm-popover
                    :target="uniqueId(`paciente-icon-${item.tipoIdentificador}-${item.id}`)"
                    triggers="hover"
                    placement="top"
                    title="Dados cadastrais"
                >
                    <div>
                        <span>
                            <strong>Prontuário:</strong> {{ item.pacienteProntuario }}
                        </span>
                    </div>

                    <div>
                        <span>
                            <strong>Data de nascimento:</strong> {{ item.pacienteDataNascimento }}
                        </span>
                    </div>

                    <div>
                        <span>
                            <strong>Nome da mãe:</strong> {{ item.pacienteNomeMae }}
                        </span>
                    </div>
                </rkm-popover>

                <span
                    :id="uniqueId(`paciente-nome-${item.tipoIdentificador}-${item.id}`)"
                    :class="{ 'text-primary': item.pacienteIsDesconhecido }"
                >
                    {{ item.pacienteNome }}

                    <template v-if="item.pacienteNomeSocial">
                        <component :is="item.pacientePrefereNomeSocial ? 'strong' : 'span'">({{ item.pacienteNomeSocial }})</component>
                    </template>
                </span>

                <rkm-popover
                    v-if="item.pacienteIsDesconhecido"
                    :target="uniqueId(`paciente-nome-${item.tipoIdentificador}-${item.id}`)"
                    triggers="hover"
                    placement="top"
                    title="Características físicas"
                >
                    <span>
                        {{ item.pacienteCaracteristicasFisicas }}
                    </span>
                </rkm-popover>
            </template>

            <template v-slot:cell(actions)="{ item }">
                <rkm-button
                    color="info"
                    small
                    title="Imprimir (não assinado)"
                    @click="openModalImpressao($event, item, false)"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    small
                    title="Imprimir (assinado)"
                    @click="openModalImpressao($event, item, true)"
                >
                    <rkm-icon icon="print" />
                </rkm-button>

                <rkm-button
                    color="primary"
                    small
                    title="Assinar"
                    @click="handeSign(item)"
                >
                    <rkm-icon icon="signature" />
                </rkm-button>
            </template>
        </rkm-table>

        <ModalSignImpressao
            @continue="continueModal"
            :sign="true"
            :assinaturaEletronica="true"
            :additionals="data.additionals"
            ref="ModalSignImpressao"
        />

        <ModalAssinaturaEletronica
            :after-sign="afterSign"
            :ref="modalAssinaturaEletronicaRefName"
            @sign="onSign"
            v-bind="modalAssinaturaEletronicaProps"
        />

        <ModalImpressao
            ref="ModalImpressao"
            :printSigned="printSigned"
            :assinaturaEletronica="printSigned"
        />
    </div>
</template>

<script>
//Components
import ModalSignImpressao from '@/components/ambulatorio/components/impressao/Modal'
import ModalImpressao from '@/components/ambulatorio/components/impressao/Modal'

// Others
import { mapActions } from 'vuex'
import { repository, error, object, data, stringCase, ambulatorioAssinaturaEletronica, file } from '@/mixins'

export default {
    components: {
        ModalSignImpressao,
        ModalImpressao
    },
    computed: {
        fields() {

            return {
                tables: {
                    atendimentos: [
                        { key: 'dataHora', label: 'Data/hora', class: 'col-min' },
                        { key: 'tipo', label: 'Tipo', class: 'col-min' },
                        { key: 'sexo', label: 'Sexo', class: 'col-min' },
                        { key: 'paciente', label: 'Paciente' },
                        { key: 'actions', label: 'Ações', class: 'col-min text-center' },
                    ]
                }
            }
        },
        validations() {

            return {
                periodo: {
                    start: { required: true },
                    end: { required: true },
                }
            }
        }
    },
    created() {

        this.repositories = {
            impressao: this.getConectaSusRepository('impressao.ambulatorio'),
            assinaturaEletronica: this.getConectaSusRepository('ambulatorio.assinaturaEletronica'),
            atendimento: this.getConectaSusRepository('ambulatorio.atendimento'),
        }
    },
    data: () => ({
        atendimentos: [],
        printSigned: false,
    }),
    mixins: [repository, error, object, data, stringCase, ambulatorioAssinaturaEletronica, file],
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        search() {

            this.setLoading()

            this.repositories.assinaturaEletronica.datatable(this.getSearchParams())
                .then(({ data }) => this.atendimentos = data)
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        getSearchParams() {

            const params = this.clone(this.data.model)

            this.formateDates(params.periodo, ['start', 'end'], 'DD/MM/YYYY')
            this.formateDates(params, ['dataNascimento'], 'DD/MM/YYYY')

            return this.camelObjectKeysToSnakeRecursive(params)
        },
        resetForm() {

            this.data.model.periodo.start = this.clone(this.data.initials.model.periodo.start)
            this.data.model.periodo.end = this.clone(this.data.initials.model.periodo.end)
            this.data.model.sexoId = this.data.initials.model.sexoId
            this.data.model.paciente = this.data.initials.model.paciente
            this.data.model.dataNascimento = this.clone(this.data.initials.model.dataNascimento)

            this.$nextTick(() => this.$refs.Form.resetValidator())
        },
        openModalImpressao(event, item, assinado) {

            event.stopPropagation();

            const id = assinado ? item.id : item.atendimentoId

            this.printSigned = !!assinado;

            this.$nextTick(() => {
                this.$refs.ModalImpressao.open('atendimento', id);
            });
        },
        handeSign(item) {

            this.onSign = () => this.search()

            this.afterSign = (atendimentoAssinado) => {

                return this.repositories.atendimento.storeAtendimentoAssinado(item.id, {
                    atendimento_assinado: atendimentoAssinado
                })
            }

            this.$refs.ModalSignImpressao.open('atendimento', item.atendimentoId, item.id)
        },
        continueModal(impressoes, atendimentoId, id) {

            this.sign(impressoes, atendimentoId, id)
        },
    },
    props: {
        data: { type: Object, required: true }
    },
    watch: {
        'data.model': {
            deep: true,
            handler() {

                this.atendimentos = []
            }
        }
    }
}
</script>